/**
 * ChainBadge Component
 *
 * Displays a visual indicator for which blockchain a particular address belongs to.
 * Used throughout the UI to show chain context.
 */

import React from 'react';
import { getChain, getChainIcon, getChainColor } from './chainUtils';
import Tooltip from './Tooltip';

/**
 * ChainBadge component
 *
 * @param {string} chain - Chain ID (ethereum, bitcoin, bitcoincash, litecoin)
 * @param {string} style - Badge style: 'pill' (default), 'minimal', 'icon-only'
 * @param {string} size - Badge size: 'sm', 'md' (default), 'lg'
 */
const ChainBadge = ({ chain, style = 'pill', size = 'md' }) => {
  if (!chain) return null;

  const chainConfig = getChain(chain);
  if (!chainConfig) return null;

  const icon = getChainIcon(chain);
  const color = getChainColor(chain);

  // Size classes
  const sizeClasses = {
    sm: 'text-xs px-2 py-0.5',
    md: 'text-sm px-2.5 py-1',
    lg: 'text-base px-3 py-1.5',
  };

  // Style variants
  if (style === 'icon-only') {
    return (
      <Tooltip text={chainConfig.name}>
        <span
          className={`inline-flex items-center justify-center ${sizeClasses[size]}`}
          style={{ color }}
        >
          {icon}
        </span>
      </Tooltip>
    );
  }

  if (style === 'minimal') {
    return (
      <Tooltip text={chainConfig.name}>
        <span
          className={`inline-flex items-center gap-1 ${sizeClasses[size]}`}
          style={{ color }}
        >
          <span>{icon}</span>
          <span className="font-semibold uppercase">{chainConfig.symbol}</span>
        </span>
      </Tooltip>
    );
  }

  // Default: pill style
  return (
    <Tooltip text={chainConfig.name}>
      <span
        className={`inline-flex items-center gap-1 rounded-full font-semibold uppercase ${sizeClasses[size]}`}
        style={{
          backgroundColor: color,
          color: 'white',
        }}
      >
        <span>{icon}</span>
        <span>{chainConfig.symbol}</span>
      </span>
    </Tooltip>
  );
};

export default ChainBadge;
